import React, { useState } from 'react';
import { 
  ArrowLeft, 
  AppWindow, 
  Cloud, 
  Database, 
  Brain, 
  FileText, 
  Shield, 
  Settings, 
  Users, 
  HelpCircle,
  X
} from 'lucide-react';

interface SettingsPageProps {
  onBack: () => void;
}

const SettingsPage: React.FC<SettingsPageProps> = ({ onBack }) => {
  const [toast, setToast] = useState<string | null>(null);

  const showComingSoon = (section: string) => {
    setToast(`${section} - Coming Soon`);
    setTimeout(() => setToast(null), 2000);
  };

  const settingsSections = [
    { id: 'applications', name: 'Applications', icon: AppWindow, description: 'Manage connected security applications' },
    { id: 'cloud', name: 'Cloud', icon: Cloud, description: 'Cloud provider configurations' },
    { id: 'datasources', name: 'Data Sources', icon: Database, description: 'Configure data ingestion sources' },
    { id: 'aiparams', name: 'AI Parameters', icon: Brain, description: 'Tune AI model settings and thresholds' },
    { id: 'reporting', name: 'Reporting', icon: FileText, description: 'Report templates and scheduling' },
    { id: 'security', name: 'Security', icon: Shield, description: 'Security policies and access controls' },
    { id: 'settings', name: 'Settings', icon: Settings, description: 'General platform settings' },
    { id: 'users', name: 'Users', icon: Users, description: 'User management and roles' },
    { id: 'help', name: 'Help', icon: HelpCircle, description: 'Documentation and support' },
  ];

  return (
    <div className="min-h-screen bg-arbitium-darker font-nunito">
      {/* Background effects */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-arbitium-primary/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-arbitium-accent/5 rounded-full blur-3xl"></div>
      </div>

      {/* Header */}
      <header className="relative z-10 bg-arbitium-dark/80 backdrop-blur-xl border-b border-arbitium-primary/10 px-6 py-4">
        <div className="flex items-center gap-4">
          <button
            onClick={onBack}
            className="p-2 text-gray-400 hover:text-white hover:bg-arbitium-primary/10 rounded-lg transition-colors"
            title="Back to Dashboard"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3">
            <Settings className="w-6 h-6 text-arbitium-accent" />
            <h1 className="text-xl font-semibold text-white">Settings</h1>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 p-6">
        <div className="max-w-6xl mx-auto">
          <p className="text-gray-400 mb-6">Configure your Arbitium platform settings and integrations.</p>
          
          {/* Settings Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {settingsSections.map((section) => (
              <button
                key={section.id}
                onClick={() => showComingSoon(section.name)}
                className="bg-arbitium-dark/50 backdrop-blur-xl border border-arbitium-primary/10 rounded-xl p-6 text-left hover:border-arbitium-accent/50 hover:bg-arbitium-dark/70 transition-all group"
              >
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-lg bg-arbitium-primary/10 text-arbitium-accent group-hover:bg-arbitium-accent/20 transition-colors">
                    <section.icon className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-white font-medium mb-1">{section.name}</h3>
                    <p className="text-sm text-gray-500">{section.description}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </main>

      {/* Toast Notification */}
      {toast && (
        <div className="fixed bottom-6 right-6 z-50 animate-fade-in">
          <div className="bg-arbitium-dark border border-arbitium-accent/50 rounded-lg px-4 py-3 flex items-center gap-3 shadow-lg">
            <span className="text-arbitium-accent">ℹ️</span>
            <span className="text-white">{toast}</span>
            <button onClick={() => setToast(null)} className="text-gray-400 hover:text-white">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsPage;
